import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BusinessPartnersService } from '../../services/business-partners.service';
import { Provider } from '../../../model/provider';

@Component({
  selector: 'app-provider-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './provider-selector.html',
  styleUrl: './provider-selector.css'
})
export class ProviderSelector implements OnInit, OnChanges, OnDestroy {
  @Input() placeholder: string = 'provider_selector.select_provider';
  @Input() businessPartnerId: number | null = null;
  @Output() providerChange = new EventEmitter<Provider | null>();

  private static readonly STORAGE_KEY = 'provider:lastSelectedId';

  protected open: boolean = false;
  protected searchTerm: string = '';
  protected selected: Provider | null = null;
  protected providers: Provider[] = [];
  protected loading: boolean = false;
  protected hasError: boolean = false;
  private initializedSelection: boolean = false;
  private lastBusinessPartnerId: number | null = null;
  private readonly subscriptions = new Subscription();

  constructor(private readonly referenceData: BusinessPartnersService) {}

  ngOnInit(): void {
    this.subscriptions.add(this.referenceData.providers$.subscribe(() => this.recomputeProviders()));
    this.subscriptions.add(this.referenceData.partnerProvidersByBpId$.subscribe(() => this.recomputeProviders()));
    this.subscriptions.add(this.referenceData.providersLoading$.subscribe((loading) => {
      this.loading = loading;
    }));
    this.subscriptions.add(this.referenceData.providersError$.subscribe((error) => {
      this.hasError = !!error;
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('businessPartnerId' in changes && !changes['businessPartnerId'].firstChange) {
      this.recomputeProviders();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected toggleOpen(): void {
    if (this.isDisabled) {
      this.open = false;
      return;
    }
    this.open = !this.open;
  }

  protected onSearchChange(value: string): void {
    this.searchTerm = value;
  }

  protected clearSearch(): void {
    this.searchTerm = '';
  }

  protected selectProvider(provider: Provider): void {
    this.selected = provider;
    this.persistStoredSelection(provider.Id);
    this.open = false;
    this.providerChange.emit(provider);
  }

  protected clearSelection(): void {
    this.selected = null;
    this.clearStoredSelection();
    this.providerChange.emit(null);
  }

  protected get filteredProviders(): Provider[] {
    const query = (this.searchTerm || '').trim().toLowerCase();
    if (!query) {
      return this.providers;
    }

    return this.providers.filter((provider) =>
      String(provider.Name || '').toLowerCase().includes(query)
    );
  }

  @HostListener('document:click')
  protected onDocumentClick(): void {
    this.open = false;
  }

  protected get isDisabled(): boolean {
    return this.businessPartnerId == null;
  }

  private recomputeProviders(): void {
    if (this.businessPartnerId == null) {
      this.providers = [];
      this.initializedSelection = false;
      this.lastBusinessPartnerId = null;
      this.selected = null;
      this.open = false;
      this.providerChange.emit(null);
      return;
    }

    const fromScope = this.referenceData.getProvidersForBusinessPartner(this.businessPartnerId);

    this.providers = (fromScope || []).slice();
    if (!this.initializedSelection || this.businessPartnerId !== this.lastBusinessPartnerId) {
      this.open = false;
      this.tryInitializeSelection();
      return;
    }

    if (this.selected && !this.providers.some((provider) => provider.Id === this.selected?.Id)) {
      this.tryInitializeSelection();
    }
  }

  private tryInitializeSelection(): void {
    if (this.providers.length === 0) {
      this.initializedSelection = true;
      this.lastBusinessPartnerId = this.businessPartnerId;
      this.selected = null;
      this.open = false;
      this.providerChange.emit(null);
      return;
    }

    this.initializedSelection = true;
    this.lastBusinessPartnerId = this.businessPartnerId;
    let selected: Provider | undefined;
    try {
      const storedRaw = localStorage.getItem(ProviderSelector.STORAGE_KEY) || '';
      const storedId = Number(storedRaw);
      if (Number.isFinite(storedId)) {
        selected = this.providers.find((provider) => provider.Id === storedId);
      }
    } catch {}

    if (!selected && this.providers.length === 1) {
      // Auto-select only when the business partner has a single provider.
      selected = this.providers[0];
    }
    if (!selected) {
      // Multiple providers without a valid stored selection: require an explicit pick.
      this.selected = null;
      this.providerChange.emit(null);
      return;
    }
    this.selected = selected;
    this.persistStoredSelection(selected.Id);
    this.providerChange.emit(selected);
  }

  private persistStoredSelection(providerId: number): void {
    try {
      localStorage.setItem(ProviderSelector.STORAGE_KEY, String(providerId));
    } catch {}
  }

  private clearStoredSelection(): void {
    try {
      localStorage.removeItem(ProviderSelector.STORAGE_KEY);
    } catch {}
  }
}

